import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { EventBus } from "@engine/EventBus.js";
import { getCombatManager } from "@engine/combat/combatManagerRef.js";
import { PartyStore } from "@store/PartyStore.js";
import { getRendererContext } from "../rendererContext.js";
import { damageColor, HEAL_COLOR } from "../damageColors.js";

/**
 * EOB2-style floating damage numbers over enemy sprites.
 *
 * Every `combat:damage_taken` on an enemy spawns an element-colored
 * splat + number anchored to the sprite (sub-cell pack offsets
 * included), which rises and fades over ~0.9 s. Anchors re-project
 * every RAF frame — same pattern as `EnemyMeter` — so popups ride a
 * moving sprite; when the sprite vanishes (death fade) the popup keeps
 * its last screen position and finishes there.
 *
 * Party-side hits render over the portraits instead — see the popup
 * overlay in `PartyBar`.
 */

const LIFETIME_MS = 1100;
const RISE_PX = 38;
// Anchor ON the sprite body (EOB2 splats hit the monster, not the air
// above it) — slightly below center so the number crosses the chest as
// it rises. The cooldown meter owns the space above the head.
const ANCHOR_OFFSET_Y = -0.08;

interface Popup {
  readonly id: number;
  readonly instanceId: string;
  readonly amount: number;
  readonly splat: string;
  readonly text: string;
  readonly heal: boolean;
  readonly bornAt: number;
  readonly jitterX: number;
  readonly rot: number;
  left: number;
  top: number;
  hasScreenPos: boolean;
}

/**
 * Damage: irregular eight-lobed splat, rotated per-popup for variety.
 * Heal: soft petal burst — reads as restorative, never as a hit.
 */
export function SplatBlob({ fill, rot, soft }: { fill: string; rot: number; soft?: boolean }) {
  return (
    <svg
      width={46}
      height={40}
      viewBox="0 0 46 40"
      style={{ position: "absolute", inset: 0, transform: `rotate(${rot}deg)` }}
    >
      {soft ? (
        <g fill={fill} opacity={0.9}>
          <circle cx={23} cy={12} r={7} />
          <circle cx={33} cy={17} r={7} />
          <circle cx={33} cy={26} r={7} />
          <circle cx={23} cy={30} r={7} />
          <circle cx={13} cy={26} r={7} />
          <circle cx={13} cy={17} r={7} />
          <circle cx={23} cy={21} r={10} />
        </g>
      ) : (
        <>
          <path
            d="M23 3 L28 10 L37 6 L33 15 L43 18 L34 23 L39 33 L29 28 L24 38 L19 29 L9 34 L13 24 L3 20 L12 15 L8 6 L17 10 Z"
            fill={fill}
            opacity={0.92}
          />
          <circle cx={40} cy={9} r={2.4} fill={fill} opacity={0.8} />
          <circle cx={6} cy={28} r={1.8} fill={fill} opacity={0.8} />
        </>
      )}
    </svg>
  );
}

let popupSeq = 0;

export function DamagePopups() {
  const [popups, setPopups] = useState<readonly Popup[]>([]);
  const popupsRef = useRef<Popup[]>([]);
  const projBufRef = useRef(new THREE.Vector3());

  useEffect(() => {
    const spawn = (
      instanceId: string,
      amount: number,
      colors: { splat: string; text: string },
      heal: boolean,
    ): void => {
      // Party-side popups render on the portraits (PartyBar), not in-world.
      if (PartyStore.getMember(instanceId)) return;
      if (amount <= 0) return;
      const seq = popupSeq++;
      popupsRef.current = [
        ...popupsRef.current,
        {
          id: seq,
          instanceId,
          amount,
          splat: colors.splat,
          text: colors.text,
          heal,
          bornAt: performance.now(),
          // Deterministic per-popup variety; staggers rapid multi-hits.
          jitterX: ((seq % 3) - 1) * 12,
          rot: (seq * 53) % 360,
          left: 0,
          top: 0,
          hasScreenPos: false,
        },
      ];
    };
    const off1 = EventBus.on("combat:damage_taken", ({ instanceId, amount, damageType }) => {
      spawn(instanceId, amount, damageColor(damageType), false);
    });
    const off2 = EventBus.on("combat:healed", ({ instanceId, amount }) => {
      spawn(instanceId, amount, HEAL_COLOR, true);
    });
    return () => {
      off1();
      off2();
    };
  }, []);

  useEffect(() => {
    let raf = 0;
    const step = () => {
      const now = performance.now();
      const cm = getCombatManager();
      const ctx = getRendererContext();
      let live = popupsRef.current.filter((p) => now - p.bornAt < LIFETIME_MS);

      if (ctx) {
        const rect = ctx.canvas.getBoundingClientRect();
        for (const p of live) {
          const pos = cm?.getEnemyWorldPos(p.instanceId);
          if (!pos) continue; // dead/gone — keep last projected point
          const v = projBufRef.current;
          v.set(pos.x, pos.y + ANCHOR_OFFSET_Y, pos.z);
          v.project(ctx.camera);
          if (v.z > 1 || v.z < -1) continue;
          p.left = rect.left + ((v.x + 1) / 2) * rect.width + p.jitterX;
          p.top = rect.top + ((1 - v.y) / 2) * rect.height;
          p.hasScreenPos = true;
        }
      }
      live = live.filter((p) => p.hasScreenPos);
      popupsRef.current = live;
      setPopups((prev) => (prev.length === 0 && live.length === 0 ? prev : [...live]));
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  if (popups.length === 0) return null;

  const now = performance.now();
  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 130 }}>
      {popups.map((p) => {
        const t = Math.min(1, (now - p.bornAt) / LIFETIME_MS);
        const rise = -RISE_PX * (1 - (1 - t) * (1 - t)); // ease-out
        const opacity = t < 0.55 ? 1 : 1 - (t - 0.55) / 0.45;
        const scale = t < 0.12 ? 0.6 + (t / 0.12) * 0.4 : 1; // pop-in
        return (
          <div
            key={p.id}
            data-testid="damage-popup"
            style={{
              position: "absolute",
              left: p.left - 23,
              top: p.top - 20 + rise,
              width: 46,
              height: 40,
              opacity,
              transform: `scale(${scale})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SplatBlob fill={p.splat} rot={p.heal ? 0 : p.rot} soft={p.heal} />
            <span
              style={{
                position: "relative",
                color: p.text,
                fontFamily: "monospace",
                fontWeight: 700,
                fontSize: p.amount >= 10 ? 15 : 16,
                textShadow: "0 1px 2px rgba(0,0,0,0.9)",
              }}
            >
              {p.heal ? `+${p.amount}` : p.amount}
            </span>
          </div>
        );
      })}
    </div>
  );
}
